import type { Adjustment } from '@/types/calendar'
import { formatRateSym } from '@/lib/calendar-utils'

/** Human-readable label for each pipeline stage */
export const STAGE_LABELS: Record<string, string> = {
  base_rate: 'Base Rate',
  linked_parent: 'Linked to Parent',
  seasonal: 'Seasonal',
  day_of_week: 'Day of Week',
  property_demand: 'Property Demand',
  room_demand: 'Room Demand',
  demand: 'Demand',
  target_pressure: 'Target Pressure',
  clearance: 'Clearance',
  event: 'Event',
  orphan_day: 'Orphan/Adjacent',
  clamp: 'Clamp',
  rounding: 'Rounding',
  rate_stability: 'Rate Stability',
  override: 'Override',
  restrictions: 'Restriction Override',
  // Combined inventory: a Same room's price is its twin's, copied. The backend's description names
  // the twin ("Same room as Double") and is preferred by stageLabel.
  same_room: 'Same room',
  // Its offset from that twin, when it carries one ("Offset from Double", the backend's description).
  same_room_offset: 'Offset',
}

/**
 * Stages that run after the engine has finished deciding a price.
 *
 * Mirrors `ComputedRate::POST_ENGINE_STAGES` — keep the two in step. `rate_stability`
 * belongs here because it guards the published price against sudden movement rather than
 * helping work the price out, so the "Computed" figure an operator sees (and that a
 * percentage override is applied to) must be read from before it.
 *
 * `clearance` is deliberately NOT here. It decides a price rather than defending one, so
 * "Computed" INCLUDES the clearance step and a percentage override multiplies the cleared
 * figure — see docs/plans/2026-08-13-last-minute-clearance.md §5.
 */
export const POST_ENGINE_STAGES: readonly string[] = ['override', 'rate_stability', 'restrictions']

/**
 * The engine's own price: the running total of the last stage that ran before any
 * post-engine stage. Returns null when the trace has no engine stage at all.
 */
export function engineComputedTotal(steps: { stage: string; runningTotal: number }[]): number | null {
  for (let i = steps.length - 1; i >= 0; i--) {
    if (!POST_ENGINE_STAGES.includes(steps[i].stage)) return steps[i].runningTotal
  }
  return null
}

export function stageLabel(stage: string, description: string): string {
  if (stage === 'seasonal') {
    const parts = description.split(' ').slice(0, -1).join(' ')
    return parts ? `Seasonal: ${parts}` : 'Seasonal'
  }
  if (stage === 'clamp') {
    return description.includes('floor') ? 'Min Rate Floor' : 'Max Rate Ceiling'
  }
  if (stage === 'event') {
    // Plain-language rule: no bare multiplier (`x1.5`) on an operator surface. The stage NAME
    // stays "Event"; the size of the uplift is shown by `stageEffect` as a plain "+N%".
    return 'Event'
  }
  if (stage === 'property_demand') {
    return 'Property Demand'
  }
  if (stage === 'room_demand') {
    return 'Room Demand'
  }
  if (stage === 'same_room' || stage === 'same_room_offset') {
    return description || STAGE_LABELS[stage]
  }
  if (stage === 'rate_stability') {
    if (!description) return 'Rate Stability'
    // "Decrease dampened to 5% per cycle" / "Increase capped at 15% per cycle"
    // -> "Decrease dampened" / "Increase capped" (cap detail moves to the tooltip)
    return description.replace(/ (to|at) \d+%.*$/, '')
  }
  return STAGE_LABELS[stage] || description || stage
}

/**
 * Signed, human-readable label for a linked child's parent offset.
 * `%` form is shown for percentage offsets, the currency form otherwise.
 * Reads `offset_value`/`offset_type` straight off the backend adjustment —
 * no math, just formatting.
 */
export function linkedOffsetLabel(adj: Adjustment, sym: string): string {
  const v = adj.offset_value ?? 0
  const sign = v >= 0 ? '+' : '-'
  if (adj.offset_type === 'absolute') {
    return `${sign}${formatRateSym(Math.abs(v), sym)}`
  }
  return `${sign}${Math.abs(v)}%`
}

/**
 * The percentage a RELATIVE override carries, or null for a fixed one.
 *
 * Reads the backend-stamped override adjustment — the two override modes are
 * only distinguishable by their trace, and every calendar surface needs the same
 * answer so an operator can tell "pinned at £132" from "+10% of whatever this
 * day computes to" without opening the day.
 */
export function overridePct(adjustments: Adjustment[] | undefined): number | null {
  const adj = adjustments?.find((a) => a.stage === 'override')
  if (!adj || adj.type !== 'percentage' || adj.modifier_pct == null) return null
  return adj.modifier_pct
}

/** "+10%" / "-15%" for a relative override. Formatting only. */
export function overridePctLabel(pct: number): string {
  return `${pct > 0 ? '+' : ''}${pct}%`
}

/** Format the effect of a stage adjustment */
export function stageEffect(adj: Adjustment, sym: string): string {
  const val = adj.value ?? adj.modifier_pct ?? 0
  switch (adj.stage) {
    case 'base_rate':
      return formatRateSym(val, sym)
    case 'linked_parent':
      return linkedOffsetLabel(adj, sym)
    case 'same_room_offset':
      // The server's spelling of the offset; the signed format of the linked offset for a trace
      // written before it carried one.
      return adj.offset_label ?? linkedOffsetLabel(adj, sym)
    case 'seasonal':
    case 'day_of_week':
      if (adj.type === 'percentage') {
        return `${val >= 0 ? '+' : ''}${val}%`
      }
      return `${val >= 0 ? '+' : ''}${formatRateSym(Math.abs(val), sym)}`
    case 'demand':
      if (adj.type === 'event_deferred') return 'Deferred to event'
      // fall through to shared percentage handling
    case 'property_demand':
    case 'room_demand':
    case 'target_pressure':
    case 'clearance':
    case 'event':
    case 'orphan_day':
      if (adj.type === 'none' || val === 0) return 'No effect'
      return `${val >= 0 ? '+' : ''}${val}%`
    case 'rate_stability': {
      const computed = adj.computed_rate
      const applied = adj.applied_rate
      if (computed != null && applied != null && computed !== applied) {
        return `${formatRateSym(computed, sym)} → ${formatRateSym(applied, sym)}`
      }
      return 'No change'
    }
    case 'clamp':
      return formatRateSym(val, sym)
    case 'rounding': {
      const rounded = adj.rounded
      const original = adj.original
      if (rounded != null && original != null) {
        const delta = rounded - original
        if (delta === 0) return 'No change'
        return `${delta >= 0 ? '+' : ''}${formatRateSym(Math.abs(delta), sym)}`
      }
      return formatRateSym(val, sym)
    }
    case 'override':
      return formatRateSym(val, sym)
    case 'same_room':
      return formatRateSym(adj.copied_rate ?? adj.running_total ?? 0, sym)
    case 'restrictions':
      return `${val} active`
    default:
      return `${val}`
  }
}

/**
 * The `same_room` step of a Same room's trace (combined inventory), or null for every other room. A
 * Same room's price is its twin's published price, copied whole: its trace has this one step, and
 * the breakdowns show it in place of a base rate and adjustments.
 */
export function sameRoomAdjustment(adjustments: Adjustment[] | undefined): Adjustment | null {
  return adjustments?.find((adj) => adj.stage === 'same_room') ?? null
}

/**
 * The twin's own engine figure, when it differs from the price the Same room copied (the twin's
 * published price moved after the engine, by hand or by rate stability). Null when they agree, so a
 * breakdown only adds the line when it says something. A comparison, never arithmetic.
 */
export function sameRoomEngineRate(adj: Adjustment): number | null {
  if (adj.engine_rate == null || adj.copied_rate == null) return null
  return adj.engine_rate === adj.copied_rate ? null : adj.engine_rate
}

/** "Engine price at Double": the label of that line. */
export function sameRoomEngineLabel(twinName: string | undefined): string {
  return twinName ? `Engine price at ${twinName}` : 'Engine price'
}

/** A single step with its running total after application */
export interface RunningTotalStep {
  stage: string
  label: string
  effect: string
  runningTotal: number
  colorClass: string
  adjustment: Adjustment
}

/**
 * Walk the adjustments array and read the backend-stamped running_total for each stage.
 * Returns an array of steps suitable for rendering grouped breakdowns.
 *
 * The backend pipeline stamps `running_total` on every adjustment — this function
 * NEVER recomputes rates. It only reads what the backend provided.
 */
export function computeRunningTotals(adjustments: Adjustment[], sym: string): RunningTotalStep[] {
  const steps: RunningTotalStep[] = []

  for (const adj of adjustments) {
    const val = adj.value ?? adj.modifier_pct ?? 0

    // Skip no-effect stages (except base_rate which always shows)
    if (adj.stage !== 'base_rate' && adj.type === 'none' && val === 0) continue

    steps.push({
      stage: adj.stage,
      label: stageLabel(adj.stage, adj.description),
      effect: stageEffect(adj, sym),
      runningTotal: adj.running_total ?? 0,
      colorClass: effectColor(adj),
      adjustment: adj,
    })
  }

  return steps
}

export function effectColor(adj: Adjustment): string {
  const val = adj.value ?? adj.modifier_pct ?? 0
  if (adj.stage === 'base_rate') return 'text-foreground'
  if (adj.stage === 'linked_parent' || adj.stage === 'same_room_offset') {
    const off = adj.offset_value ?? 0
    if (off === 0) return 'text-muted-foreground'
    return off > 0 ? 'text-emerald-600' : 'text-rose-600'
  }
  if (adj.stage === 'clamp') return 'text-amber-600'
  if (adj.stage === 'rounding') {
    const delta = (adj.rounded ?? 0) - (adj.original ?? 0)
    if (delta === 0) return 'text-muted-foreground'
    return delta > 0 ? 'text-emerald-600' : 'text-rose-600'
  }
  if (adj.stage === 'override') return 'text-blue-600'
  if (adj.stage === 'event') return 'text-violet-600'
  if (adj.stage === 'rate_stability') {
    const delta = (adj.applied_rate ?? 0) - (adj.computed_rate ?? 0)
    if (delta === 0) return 'text-muted-foreground'
    return delta > 0 ? 'text-amber-600' : 'text-amber-600'
  }
  if (adj.type === 'none' || val === 0) return 'text-muted-foreground'
  return val > 0 ? 'text-emerald-600' : 'text-rose-600'
}
